<template>
  <EdroneModal
    :open="show"
    close-on-esc-key-press
    close-on-outside-click
    show-close-icon
    :width="400"
    @close="$emit('close')"
  >
    <template #title>{{ translations.drawer.confirm.title }}</template>
    <div class="tw:flex tw:flex-col tw:gap-6">
      <template v-if="activePackage && subscription">
        <p class="tw:body-2 tw:text-default-700">
          {{ translations.drawer.confirm.descriptionReplace }}
        </p>
        <div class="tw:flex tw:gap-4 tw:p-3">
          <div class="tw:flex-1">
            <p class="tw:body-2 tw:mb-1">{{ translations.drawer.confirm.current }}</p>
            <p class="tw:heading-2">
              {{ subscription.packageSize }}
            </p>
            <p class="tw:caption">
              {{
                formatNumberV2((subscription.packageSize || 0) * (activePackage?.price || 0), {
                  currency: currency,
                  style: "currency",
                })
              }}
              / {{ translations.drawer.confirm.month }}
            </p>
          </div>
          <div class="tw:text-2xl tw:self-center tw:p-3">
            <font-awesome-icon icon="fa-light fa-arrow-right" />
          </div>
          <div class="tw:flex-1">
            <p class="tw:body-2 tw:mb-1">{{ translations.drawer.confirm.new }}</p>
            <p class="tw:heading-2">
              {{ newPackageSize }}
            </p>
            <p class="tw:caption">
              {{
                formatNumberV2(packageCost - getCouponDiscount(packageCost), {
                  currency: currency,
                  style: "currency",
                })
              }}
              / {{ translations.drawer.confirm.month }}
            </p>
          </div>
        </div>
      </template>
      <template v-else>
        <p class="tw:body-2 tw:text-default-700">
          {{
            translations.drawer.confirm.descriptionNew
              .replace("{package}", formatNumberV2(newPackageSize))
              .replace(
                "{price}",
                `${formatNumberV2(packageCost - getCouponDiscount(packageCost), { style: "currency", currency })}`
              )
          }}
        </p>
        <div class="tw:flex tw:gap-4 tw:p-3">
          <div class="tw:flex-1">
            <p class="tw:body-2 tw:mb-1">{{ translations.drawer.confirm.new }}</p>
            <p class="tw:heading-2">
              {{ newPackageSize }}
            </p>
            <p class="tw:caption">
              {{ formatNumberV2(packageCost - getCouponDiscount(packageCost), { style: "currency", currency }) }}
              / {{ translations.drawer.confirm.month }}
            </p>
          </div>
        </div>
      </template>
      <div class="tw:flex tw:gap-2">
        <EdroneButton class="flex-fill" type="outline" :disabled="loading" @click="emit('close')">
          {{ translations.drawer.confirm.buttons.cancel }}
        </EdroneButton>
        <EdroneButton class="flex-fill" type="solid" :disabled="loading" :loading="loading" @click="emit('confirm')">
          {{ translations.drawer.confirm.buttons.confirm }}
        </EdroneButton>
      </div>
    </div>
  </EdroneModal>
</template>

<script setup lang="ts">
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneModal from "edrone-components/EdroneModal.vue";
import { formatNumberV2 } from "vue-app/utils/functions.ts";
import { computed, Ref } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { DTOPromotionCodeResponse, DTOTierPrice } from "vue-app/api/dto.d";
import { getPackage } from "vue-app/components/PackageManager/PackageManager.helpers";
import { Translations } from "vue-app/types/translations";
import { PackageManagerData } from "vue-app/components/PackageManager/PackageManager.types";

interface Props {
  show: boolean;
  loading?: boolean;
  translations: Translations["_packageManager"]["sms" | "whatsapp"];
  currency?: string;
  newPackageSize: number;
  price: number;
  subscription: PackageManagerData["subscription"];
  packages: PackageManagerData["packages"];
  coupon: DTOPromotionCodeResponse | null;
}
interface Emits {
  (event: "close"): void;
  (event: "confirm"): void;
}
const props = withDefaults(defineProps<Props>(), {
  show: false,
  isLoading: false,
  currency: "PLN",
});
const emit = defineEmits<Emits>();

const packageCost = computed(() => props.newPackageSize * props.price);

const activePackage: Ref<DTOTierPrice | null> = computed(() => {
  if (props.subscription) {
    return getPackage(props.subscription?.packageSize || 0, props.packages?.tierPrices || []);
  }
  return null;
});

function getCouponDiscount(price: number): number {
  if (!props.coupon?.active || props.coupon?.duration === "ONCE") {
    return 0;
  }
  const discount = props.coupon.amountOff || (props.coupon.percentOff ? (price * props.coupon.percentOff) / 100 : 0);
  return discount > price ? price : discount;
}
</script>
